/**
 * BOQ hooks (React Query)
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { getBOQById, getProjectBOQs } from "../services/boq/boq-api.service";

interface ProjectBOQsQueryOptions {
	enabled?: boolean;
	refetchOnMount?: boolean | "always";
}

export function useProjectBOQs(
	projectId: string,
	{ enabled = true, refetchOnMount }: ProjectBOQsQueryOptions = {},
) {
	return useQuery({
		queryKey: queryKeys.projects.boq(projectId),
		queryFn: async () => {
			const result = await getProjectBOQs(projectId);
			if (result.error) {
				throw Object.assign(new Error(result.error), {
					forbidden: result.forbidden === true,
				});
			}
			return result.data;
		},
		enabled,
		refetchOnMount,
	});
}

/**
 * One BOQ with its sections and lines. Not retried: `getBOQById` keeps the API
 * client's error toast, so a retry would toast a failed load twice.
 */
export function useBOQDetails(boqId: string, enabled = true) {
	return useQuery({
		queryKey: ["boq", boqId],
		queryFn: async () => {
			const result = await getBOQById(boqId);
			if (result.error) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled,
		retry: false,
	});
}
